import { ROLE_GROUPS } from '@/shared/auth';
import { PRIVILEGES } from '@stayon/auth-core';
import type { SettingsNavGroup } from './settingsNavItem';

export type { SettingsNavGroup, SettingsNavItem } from './settingsNavItem';

const { workspaceAdmin, platformOnly } = ROLE_GROUPS;

/**
 * Interne Seiten-Navigation des Einstellungen-Hubs. Eine Quelle für die Sub-Sidebar
 * (SettingsLayout), die Hub-Übersicht (SettingsHubPage) UND die Aktiv-Erkennung des
 * Haupt-Nav-Eintrags „Einstellungen" (isSettingsPath) — damit driften Nav, Layout und
 * Route-Guards nicht auseinander.
 *
 * Die „Konten der Buchungsportale" sind die kontoweiten Channex-Verbindungen; welches
 * Apartment zu welcher Hotel-ID gehört, bleibt am Apartment.
 */
export const settingsNavigation: SettingsNavGroup[] = [
  {
    label: 'Organisation & Zugriff',
    items: [
      {
        to: '/settings/workspace',
        label: 'Workspace',
        description: 'Name, Zeitzone, Währung und Kontaktadresse',
        icon: 'Settings',
        roles: workspaceAdmin,
      },
      {
        to: '/users',
        label: 'Benutzer',
        description: 'Konten, Rollen und Einladungen',
        icon: 'Users',
        roles: workspaceAdmin,
      },
      {
        to: '/companies',
        label: 'Firmen',
        description: 'Kunden- und Dienstleister-Firmen',
        icon: 'Building',
        roles: workspaceAdmin,
      },
      {
        to: '/cleaning-teams',
        label: 'Reinigungsteams',
        description: 'Leitungen, Mitglieder und Objektfreigaben',
        icon: 'Users',
        privilege: PRIVILEGES.cleaningTeamsManage,
      },
    ],
  },
  {
    label: 'Abrechnung',
    items: [
      {
        to: '/settings/invoice-templates',
        label: 'Rechnungsvorlagen',
        description: 'Absenderdaten, Bankverbindung und Positionskatalog',
        icon: 'Database',
        roles: workspaceAdmin,
      },
      {
        to: '/settings/tax',
        label: 'Steuer und MwSt',
        description: 'Steuersätze und Umsatzsteuer',
        icon: 'Chart',
        roles: workspaceAdmin,
      },
    ],
  },
  {
    label: 'Integrationen',
    items: [
      {
        to: '/settings/stripe',
        label: 'Stripe',
        description: 'Zahlungs-Provider und API-Schlüssel',
        icon: 'Webhook',
        roles: workspaceAdmin,
      },
      {
        to: '/settings/integrations',
        label: 'Konten der Buchungsportale',
        description: 'Kontoweite Verbindungen zu den Buchungsportalen',
        icon: 'Globe',
        roles: workspaceAdmin,
        platformWorkspaceOnly: true,
      },
    ],
  },
  {
    label: 'Smart Lock',
    items: [
      {
        to: '/smart-lock/devices',
        label: 'Geräte',
        description: 'Verbundene Schlösser und ihr Zustand',
        icon: 'Lock',
        roles: workspaceAdmin,
      },
      {
        to: '/smart-lock/mapping',
        label: 'Zuordnung',
        description: 'Welcher Code zu welchem Apartment gehört',
        icon: 'Sliders',
        roles: workspaceAdmin,
      },
    ],
  },
  {
    label: 'Plattform',
    items: [
      {
        to: '/platform/workspaces',
        label: 'Workspaces',
        description: 'Kunden-Workspaces anlegen und sperren',
        icon: 'Building',
        roles: platformOnly,
        platformOnly: true,
      },
    ],
  },
  {
    label: 'System & Sicherheit',
    items: [
      {
        to: '/system/audit',
        label: 'Änderungsprotokoll',
        description: 'Wer wann was geändert hat',
        icon: 'Shield',
        roles: platformOnly,
        platformOnly: true,
      },
      {
        to: '/system/outbox',
        label: 'Mail-Ausgang',
        description: 'Ausgehende Mails und ihr Zustellstatus',
        icon: 'Inbox',
        roles: platformOnly,
        platformOnly: true,
      },
      {
        to: '/system/webhook-events',
        label: 'Meldungen des Kanalmanagers',
        description: 'Eingehende Channex-Meldungen zur Fehlersuche',
        icon: 'Webhook',
        roles: platformOnly,
        platformOnly: true,
      },
    ],
  },
];
